import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/** 一覧で選んだ法人と、検索結果の中での位置（Excel の行を一覧の並びにそろえるのに使う） */
export interface CorporateSelectionEntry {
  corporateNumber: string;
  position: number;
}

/** 一覧の選択状態。法人番号 → 検索結果の中での位置 */
export interface CorporateSelection {
  selected: ReadonlyMap<string, number>;
  toggle: (
    entries: readonly CorporateSelectionEntry[],
    checked: boolean
  ) => void;
}

/** 「すべて選択」の1回分。後から検索条件の変更・選択の操作・次の「すべて選択」があったら、どの操作も何もしない */
export interface CorporateSelectionReplace {
  /** 後から別の操作があったか（遅れて返った応答・失敗を反映しないために使う） */
  isCurrent: () => boolean;
  /** 選んだ法人をすべて入れ替える */
  apply: (entries: readonly CorporateSelectionEntry[]) => void;
  /** 読み込み中を終える */
  finish: () => void;
}

interface UseCorporateSelectionResult extends CorporateSelection {
  /** 選んだ法人をすべて入れ替える準備をし、読み込み中にする（「すべて選択」） */
  beginReplace: () => CorporateSelectionReplace;
  /** 「すべて選択」の読み込み中か。後から別の操作があったら、古い読み込みの完了を待たずに外す */
  isReplacing: boolean;
  clear: () => void;
  /** 選んだ法人番号を一覧の並び順で返す */
  orderedCorporateNumbers: string[];
}

/**
 * 法人マスタ一覧の選択を持つ。ページの移動や表示形式の切替では残し、検索条件が変わったら外す
 * @param resetKey 検索条件を表す値。変わったら選択を外す
 * @returns 選択状態と操作
 */
export const useCorporateSelection = (
  resetKey: string
): UseCorporateSelectionResult => {
  const [selected, setSelected] = useState<ReadonlyMap<string, number>>(
    () => new Map()
  );
  const [isReplacing, setIsReplacing] = useState(false);
  // 検索条件の変更・選択の操作・「すべて選択」で進める番号。「すべて選択」の応答が、その後の操作より後に返っても反映しないために使う
  const generationRef = useRef(0);
  const advance = useCallback((): void => {
    generationRef.current += 1;
    setIsReplacing(false);
  }, []);

  useEffect(() => {
    advance();
    setSelected(new Map());
  }, [resetKey, advance]);

  const toggle = useCallback(
    (entries: readonly CorporateSelectionEntry[], checked: boolean): void => {
      advance();
      setSelected((current) => {
        const next = new Map(current);
        entries.forEach(({ corporateNumber, position }) => {
          if (checked) next.set(corporateNumber, position);
          else next.delete(corporateNumber);
        });
        return next;
      });
    },
    [advance]
  );

  const beginReplace = useCallback((): CorporateSelectionReplace => {
    advance();
    const generation = generationRef.current;
    setIsReplacing(true);
    const isCurrent = (): boolean => generation === generationRef.current;
    return {
      isCurrent,
      apply: (entries) => {
        if (!isCurrent()) return;
        setSelected(
          new Map(
            entries.map(({ corporateNumber, position }) => [
              corporateNumber,
              position,
            ])
          )
        );
      },
      finish: () => {
        if (isCurrent()) setIsReplacing(false);
      },
    };
  }, [advance]);

  const clear = useCallback((): void => {
    advance();
    setSelected(new Map());
  }, [advance]);

  const orderedCorporateNumbers = useMemo(
    () =>
      [...selected.entries()]
        .sort(([, a], [, b]) => a - b)
        .map(([corporateNumber]) => corporateNumber),
    [selected]
  );

  return {
    selected,
    toggle,
    beginReplace,
    isReplacing,
    clear,
    orderedCorporateNumbers,
  };
};
